import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGame, GamePhase } from "../../context/GameContext";
import ChatPanel from "../game/ChatPanel";
import PlayersList from "../game/PlayersList";
import GameInfo from "../game/GameInfo";
import ActionPanel from "../game/ActionPanel";
import VotingPanel from "../game/VotingPanel";
import GameOverModal from "../game/GameOverModal";
import StoryIntroSequence from "../game/StoryIntroSequence";
import RoleRevealOverlay from "../game/RoleRevealOverlay";
import KillNotification from "../game/KillNotification";

const GamePage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const { gameState } = useGame();
  const navigate = useNavigate();
  const [showGameOver, setShowGameOver] = useState(false);
  const [showStoryIntro, setShowStoryIntro] = useState(true);
  const [showRoleReveal, setShowRoleReveal] = useState(true);
  const [showKillNotification, setShowKillNotification] = useState<
    string | null
  >(null);
  const [previousKillResult, setPreviousKillResult] = useState<string | null>(
    null
  );

  // Check if we're in debug mode
  const isDebugMode =
    roomId?.startsWith("debug-") ||
    localStorage.getItem("debugGameEnabled") === "true";

  // Redirect if not in a valid game and not in debug mode
  useEffect(() => {
    if (
      !isDebugMode &&
      (!roomId || !gameState.roomId || gameState.roomId !== roomId)
    ) {
      navigate("/");
    }
  }, [roomId, gameState.roomId, navigate, isDebugMode]);

  // Show game over modal when game ends
  useEffect(() => {
    if (gameState.isGameOver && !showGameOver) {
      setShowGameOver(true);
    }
  }, [gameState.isGameOver, showGameOver]);

  // Hide story intro after 25 seconds (enough time to read the story)
  useEffect(() => {
    if (showStoryIntro) {
      const timer = setTimeout(() => {
        setShowStoryIntro(false);
      }, 25000);
      return () => clearTimeout(timer);
    }
  }, [showStoryIntro]);

  // Check for new kill results
  useEffect(() => {
    if (
      gameState.phase === GamePhase.DAY &&
      gameState.killResult &&
      gameState.killResult !== previousKillResult
    ) {
      // Find the victim's name
      const victim = gameState.players.find(
        (p) => p.id === gameState.killResult
      );
      if (victim) {
        setShowKillNotification(victim.name);
        setPreviousKillResult(gameState.killResult);
      }
    }
  }, [
    gameState.phase,
    gameState.killResult,
    gameState.players,
    previousKillResult,
  ]);

  // Add debug exit button
  const exitDebugMode = () => {
    localStorage.removeItem("debugGameEnabled");
    navigate("/");
  };

  // Get background class based on game phase
  const getBgClass = () => {
    if (gameState.phase === GamePhase.NIGHT) {
      return "night-bg";
    }
    return "day-bg";
  };

  // Get role-specific background class
  const getRoleBackgroundClass = () => {
    switch (gameState.myRole) {
      case "mafia":
        return "mafia-bg";
      case "doctor":
        return "doctor-bg";
      case "detective":
        return "detective-bg";
      case "villager":
        return "villager-bg";
      default:
        return "";
    }
  };

  if (!gameState.isGameStarted && !isDebugMode) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card">
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  // Show story intro before the actual game starts
  if (showStoryIntro) {
    return <StoryIntroSequence onComplete={() => setShowStoryIntro(false)} />;
  }

  // Show role reveal after story intro
  if (showRoleReveal) {
    return (
      <RoleRevealOverlay
        role={gameState.myRole}
        onComplete={() => setShowRoleReveal(false)}
      />
    );
  }

  return (
    <div
      className={`min-h-screen ${getBgClass()} ${getRoleBackgroundClass()} transition-colors duration-1000 bg-blend-overlay`}
    >
      {isDebugMode && (
        <div className="fixed top-0 left-0 right-0 bg-amber-900/80 text-amber-200 py-1 px-4 text-xs flex justify-between items-center z-50">
          <span>
            DEBUG MODE: {gameState.phase} | Role: {gameState.myRole}
          </span>
          <button onClick={exitDebugMode} className="underline">
            Exit Debug
          </button>
        </div>
      )}

      <div className="game-container">
        <div className="flex flex-col lg:flex-row gap-6 py-4">
          {/* Game information and role display */}
          <div className="lg:w-1/4">
            <GameInfo
              phase={gameState.phase}
              dayCount={gameState.dayCount}
              role={gameState.myRole}
              isAlive={
                gameState.players.find((p) => p.id === gameState.myId)
                  ?.isAlive || false
              }
              killResult={gameState.killResult}
              detectiveResult={gameState.detectiveResult}
            />

            <div className="mt-4">
              <PlayersList
                players={gameState.players}
                currentPlayerId={gameState.myId}
                phase={gameState.phase}
                myRole={gameState.myRole}
              />
            </div>
          </div>

          {/* Game action area */}
          <div className="lg:w-2/5 flex flex-col">
            {gameState.phase === GamePhase.NIGHT ? (
              <ActionPanel
                phase={gameState.phase}
                role={gameState.myRole}
                players={gameState.players.filter(
                  (p) => p.id !== gameState.myId && p.isAlive
                )}
                isAlive={
                  gameState.players.find((p) => p.id === gameState.myId)
                    ?.isAlive || false
                }
              />
            ) : (
              <VotingPanel
                players={gameState.players.filter(
                  (p) => p.id !== gameState.myId
                )}
                isAdmin={gameState.adminId === gameState.myId}
                isAlive={
                  gameState.players.find((p) => p.id === gameState.myId)
                    ?.isAlive || false
                }
              />
            )}
          </div>

          {/* Chat panel */}
          <div className="lg:w-1/3">
            <ChatPanel
              messages={gameState.messages}
              isAlive={
                gameState.players.find((p) => p.id === gameState.myId)
                  ?.isAlive || false
              }
              phase={gameState.phase}
            />
          </div>
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          winner={gameState.winner || ""}
          players={gameState.players}
          myRole={gameState.myRole}
          onClose={() => navigate("/")}
        />
      )}

      {showKillNotification && (
        <KillNotification
          victimName={showKillNotification}
          onClose={() => setShowKillNotification(null)}
        />
      )}
    </div>
  );
};

export default GamePage;
